import React from 'react';
import { Text, View } from '../../Components/Themed';
import { Modal, TouchableOpacity, FlatList, Dimensions } from 'react-native';
import { useColors } from '../../constants/useColors';
import { Icons } from '../../Components';
import type { OpportunityProps, RouterOrderProps, RouterPartnerProps } from '../types';

type PlacedOpportunityModalProps = {
    visible: boolean;
    opportunity: OpportunityProps | null;
    orders: RouterOrderProps[];
    partners: RouterPartnerProps[];
    onClose: () => void;
    insets?: { top: number; bottom: number; left: number; right: number };
};

const PlacedOpportunityModal = ({
    visible,
    opportunity,
    orders,
    partners,
    onClose,
    insets
}: PlacedOpportunityModalProps) => {
    const Colors = useColors();

    if (!opportunity) return null;

    // Use status to determine which data to show
    const isClosed = opportunity.status === 'closed';
    const profit = isClosed ? (opportunity.actual_profit || 0) : (opportunity.expected_profit || 0);
    const roi = isClosed ? (opportunity.actual_roi || 0) : (opportunity.expected_roi || 0);
    const isProfitable = profit > 0;
    const oppOrders = orders.filter(order => order.opportunity_id === opportunity.opportunity_id);

    // Calculate available height respecting insets
    const screenHeight = Dimensions.get('window').height;
    const topInset = insets?.top || 0;
    const bottomInset = insets?.bottom || 0;
    const modalPadding = 32;
    const maxModalHeight = screenHeight - topInset - bottomInset - modalPadding;

    return (
        <Modal
            visible={visible}
            transparent
            animationType="fade"
            onRequestClose={onClose}
        >
            <View style={{ flex: 1, backgroundColor: 'rgba(0,0,0,0.5)', justifyContent: 'center', alignItems: 'center', paddingTop: topInset, paddingBottom: bottomInset, paddingHorizontal: 16 }}>
                <View type='body' style={{ width: '100%', maxWidth: 500, borderRadius: 12, height: maxModalHeight }}>
                    {/* Header */}
                    <View type='header' style={{ padding: 16, borderTopLeftRadius: 12, borderTopRightRadius: 12, flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                        <View transparent style={{ flex: 1 }}>
                            <Text theme='h1' size={18}>
                                {opportunity.opportunity_type.toUpperCase()}
                            </Text>
                            <Text
                                theme='h2'
                                size={20}
                                color={isProfitable ? Colors.text.success : Colors.text.warning}
                                style={{ marginTop: 4 }}
                            >
                                ${profit.toFixed(2)} profit
                            </Text>
                        </View>
                        <TouchableOpacity onPress={onClose}>
                            <Icons.CloseIcon size={24} color={Colors.text.h1} />
                        </TouchableOpacity>
                    </View>

                    {/* Content */}
                    <View style={{ flex: 1 }}>
                        <FlatList
                            data={oppOrders}
                            keyExtractor={(item, idx) => `${item.router_order_id}-${idx}`}
                            style={{ flex: 1 }}
                            contentContainerStyle={{ padding: 16 }}
                            renderItem={({ item: order }) => {
                                const hasResult = order.result !== null && order.result !== undefined;
                                const displayWinnings = hasResult ? order.net_winnings : order.potential_payout;
                                const winningsLabel = hasResult
                                    ? (order.result === 'win' ? '✓ Won'
                                        : order.result === 'lose' ? '✗ Lost'
                                        : order.result === 'push' ? '= Draw'
                                        : order.result === 'cancelled' ? '✕ Cancelled'
                                        : order.result)
                                    : '⋯ Potential';
                                const partner = partners.find(p => p.partner_id === order.partner_id);

                                return (
                                    <View transparent style={{
                                        padding: 12,
                                        backgroundColor: Colors.views.header,
                                        borderRadius: 8,
                                        marginBottom: 12,
                                        borderWidth: 1,
                                        borderColor: Colors.borders.light
                                    }}>
                                        <View transparent style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 8 }}>
                                            <Text theme='h2' size={14}>
                                                {partner?.name || 'Unknown'}
                                            </Text>
                                            <Text theme='h2' size={14}>
                                                ${order.amount?.toFixed(2)}
                                            </Text>
                                        </View>

                                        <Text theme='description' size={13} style={{ marginBottom: 8 }}>
                                            {order.title}
                                        </Text>

                                        <View transparent style={{ flexDirection: 'row', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                                            <View transparent>
                                                <Text theme='description' size={11}>{winningsLabel}</Text>
                                                <Text theme='h2' size={12} style={{ marginTop: 2 }}>
                                                    ${displayWinnings?.toFixed(2) || '0.00'}
                                                </Text>
                                            </View>

                                            <View transparent>
                                                <Text theme='description' size={11}>Price</Text>
                                                <Text theme='h2' size={12} style={{ marginTop: 2 }}>
                                                    {((order.price || 0) * 100).toFixed(1)}%
                                                </Text>
                                            </View>

                                            <View transparent>
                                                <Text theme='description' size={11}>Status</Text>
                                                <Text theme='h2' size={12} style={{ marginTop: 2 }}>
                                                    {order.status}
                                                </Text>
                                            </View>
                                        </View>
                                    </View>
                                );
                            }}
                            ListFooterComponent={() => (
                                <View transparent style={{ marginTop: 8, paddingTop: 16, borderTopWidth: 1, borderTopColor: Colors.borders.light }}>
                                    <View transparent style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 8 }}>
                                        <Text theme='description' size={13}>{isClosed ? 'Invested' : 'Total Bet'}</Text>
                                        <Text theme='h2' size={15}>
                                            ${opportunity.total_bet_amount.toFixed(2)}
                                        </Text>
                                    </View>

                                    <View transparent style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 8 }}>
                                        <Text theme='description' size={13}>{isClosed ? 'Actual ROI' : 'Expected ROI'}</Text>
                                        <Text theme='h2' size={15}>
                                            {(roi * 100).toFixed(1)}%
                                        </Text>
                                    </View>

                                    <View transparent style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 8 }}>
                                        <Text theme='description' size={13}>Total Winnings</Text>
                                        <Text theme='h2' size={15}>
                                            ${opportunity.total_winnings.toFixed(2)}
                                        </Text>
                                    </View>

                                    <View transparent style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 8 }}>
                                        <Text theme='description' size={13}>Total Fees</Text>
                                        <Text theme='h2' size={15}>
                                            ${opportunity.total_fees.toFixed(2)}
                                        </Text>
                                    </View>

                                    <View transparent style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
                                        <Text theme='description' size={13}>{isClosed ? 'Profit' : 'Fill Status'}</Text>
                                        {isClosed ? (
                                            <Text theme='h2' size={15} color={isProfitable ? Colors.text.success : Colors.text.error}>
                                                ${profit.toFixed(2)}
                                            </Text>
                                        ) : (
                                            <Text theme='h2' size={15}>
                                                {opportunity.fill_status === 'all_filled' ? '✓ All filled' :
                                                 opportunity.fill_status === 'partially_filled' ? '⚠ Partial' :
                                                 opportunity.fill_status === 'failed' ? '✗ Failed' : '⋯ Pending'}
                                            </Text>
                                        )}
                                    </View>
                                </View>
                            )}
                        />
                    </View>
                </View>
            </View>
        </Modal>
    );
};

export default PlacedOpportunityModal;
